import { action, observable, makeObservable } from 'mobx';
import { AxiosError } from 'axios';

import DataMixin from 'utils/stores/DataMixin';

import API from 'utils/api';
import RootStore from 'stores/Root';

import { JSONSchemaType } from 'utils/api/modules/incidents/incidents.types';
import { EntityFieldType, EntityValueType, holdsMomentDate } from 'utils/api/modules/entities/entities.types';

import { EntityModules } from 'core/constants';

import { FieldType } from './types';

export const DISABLED_FIELD_SUFFIX = '-disabled';
export const PIPELINE_STAGE_ROLE = 'pipeline_stage';

export default class FieldsStore extends DataMixin {
  @observable error?: AxiosError;

  @observable data: FieldType[] = [];

  @observable fields: EntityFieldType[] = [];

  @observable filteredData: FieldType[] = [];

  @observable searchQuery = '';

  @observable activeField: EntityFieldType | null = null;

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api);

    makeObservable(this);
  }

  @action.bound
  async loadFields(entityType: string, withDeleted = false): Promise<void> {
    this.isLoaded = false;
    try {
      const {
        data: { results },
      } = await this.api.entities.loadEntityFields(entityType, {
        pagination: 'false',
        include_deleted: withDeleted,
      })();

      this.fields = results;

      // modify data to match the FieldType type
      this.data = results.map(({ key, name, required, readonly, protectorType, properties, helpText }) => ({
        id: key,
        name,
        required,
        readonly,
        protectorType,
        enum: properties?.enum,
        lookupType: properties?.lookupType,
        helpText,
        momentDate: holdsMomentDate(protectorType, properties?.roles),
      }));

      this.error = undefined;
    } catch (err) {
      const e = err as AxiosError;
      this.error = e;
    } finally {
      this.isLoaded = true;
    }
  }

  @action.bound
  setActiveField(field: EntityFieldType | null): void {
    this.activeField = field;
  }

  // field manager actions
  createField = async (entityType: string, fieldValues: Partial<EntityFieldType>): Promise<void> => {
    await this.api.entities.createEntityField(entityType, fieldValues)();
  };

  editField = async (entityType: string, fieldId: string, fieldValues: Partial<EntityFieldType>): Promise<void> => {
    await this.api.entities.editEntityField(entityType, fieldId, fieldValues)();
  };

  deleteField = async (entityType: string, fieldId: string): Promise<void> => {
    await this.api.entities.deleteEntityField(entityType, fieldId)();
  };

  getNameOfField(id: string): string {
    if (!this.data) {
      return '';
    }

    const field = this.data.find((f) => f.id === id);
    const fieldDisabled = this.data.find((f) => f.id === `${id}${DISABLED_FIELD_SUFFIX}`);
    const res = field || fieldDisabled;

    return res ? res.name : `${id} not found`;
  }

  checkIfFieldIsRequired(id: string): boolean {
    const field = this.data.find(
      (f) => f.id.replace(DISABLED_FIELD_SUFFIX, '') === id.replace(DISABLED_FIELD_SUFFIX, '')
    );
    return field?.required || false;
  }

  @action.bound
  changeFieldsToDisabled(id: string[]): void {
    if (!this.data) {
      return;
    }
    this.data = this.data.map((field) => {
      if (id.includes(field.id)) {
        return {
          id: `${field.id}${DISABLED_FIELD_SUFFIX}`,
          name: field.name,
          required: field.required,
          protectorType: field.protectorType,
        };
      }
      return field;
    });
    this.filteredData = this.data;
  }

  @action.bound
  searchFields(query: string): void {
    this.searchQuery = query;
    this.filteredData = this.data?.filter((field) => field.name.toLowerCase().includes(query.toLowerCase()));
  }

  private updateField(id: string, newId: string): void {
    if (!this.data) {
      return;
    }
    const index = this.data.findIndex((field) => field.id === id);
    if (index !== -1) {
      const newData = [...this.data];
      newData[index] = {
        id: newId,
        name: this.getNameOfField(id),
        required: this.checkIfFieldIsRequired(id),
        protectorType: this.data[index].protectorType,
      };
      this.data = newData;
    }
    this.filteredData = this.data;
  }

  @action.bound
  addField(id: string): void {
    if (!this.data || this.data.some((field) => field.id === id)) {
      return;
    }
    this.updateField(`${id}${DISABLED_FIELD_SUFFIX}`, id);
  }

  @action.bound
  removeField(id: string): void {
    if (!this.data) {
      return;
    }
    this.updateField(id, `${id}${DISABLED_FIELD_SUFFIX}`);
  }

  @action.bound
  addAllFieldsFromRemovedContainer(itemsFromContainer: string[]): void {
    if (!this.data) {
      return;
    }
    itemsFromContainer.forEach((item) => {
      this.removeField(item);
      this.addField(item);
    });
  }

  @action.bound
  getFieldById(
    id: string,
    value: EntityValueType | undefined
  ):
    | {
        schema: JSONSchemaType;
        value: EntityValueType;
      }
    | undefined {
    const field = this.data.find((f) => f.id === id);
    if (!field) {
      return undefined;
    }

    const formattedField: {
      schema: JSONSchemaType;
      value: EntityValueType;
    } = {
      schema: {
        name: field.name,
        protectorType: field.protectorType,
        enum: field.enum as unknown as string[],
        lookupType: field.lookupType ? (field.lookupType as unknown as EntityModules) : undefined,
        properties: {
          required: (field.required || false) as unknown as JSONSchemaType,
        },
        type: 'string',
        description: '',
        order: [],
        readonly: field.readonly || false,
        helpText: field.helpText,
        momentDate: field.momentDate,
      },
      value: value || null,
    };

    return formattedField;
  }
}
